<script setup lang="ts">
import { computed } from 'vue'
import { ElMessage } from 'element-plus'
import type { AdminRecord } from '@/types/record'
import type { PageDefinition, SelectOption } from '@/types/page'
import { fieldLabel, formatFieldValue } from '@/utils/presentation'
import { avatarText, avatarTone } from '@/utils/presentation'
import { formatDateTime, statusTone } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  open: boolean
  definition: PageDefinition
  record?: AdminRecord | null
  canEdit?: boolean
}>()

const emit = defineEmits<{
  close: []
  edit: [record: AdminRecord]
}>()

const title = computed(() => {
  const row = props.record
  if (!row) return props.definition.title
  return String(row.name ?? row.generic_name ?? row.title ?? row.question ?? row.order_no ?? row.plan_no ?? row.ticket_no ?? row.report_no ?? row.user_name ?? row.id ?? '')
})

const subtitle = computed(() => {
  const row = props.record
  if (!row) return ''
  const candidates = ['user_no', 'family_no', 'plan_no', 'alert_no', 'report_no', 'device_no', 'drug_no', 'message_no', 'order_no', 'ticket_no', 'feedback_no', 'admin_no', 'approval_no']
  const key = candidates.find(item => row[item])
  return key ? String(row[key]) : String(row.account ?? row.category_name ?? row.region_name ?? '')
})

const statusOption = computed(() => {
  if (!props.record || !props.definition.statusKey) return null
  const raw = props.record[props.definition.statusKey]
  const option = props.definition.statusMap?.[String(raw)] ?? null
  return { value: raw, label: option?.label ?? String(raw ?? ''), tone: option?.tone ?? statusTone(String(option?.label ?? raw ?? '')) }
})

const metrics = computed(() => (props.definition.detailMetrics ?? []).map(metric => ({
  ...metric,
  value: formatFieldValue(props.record?.[metric.field], props.definition, metric.field),
})))

const sections = computed(() => (props.definition.detailSections ?? []).map(section => ({
  title: section.title,
  fields: section.fields.map(key => ({
    key,
    label: fieldLabel(key, props.definition),
    value: props.record?.[key],
    display: formatFieldValue(props.record?.[key], props.definition, key),
  })),
})))

const diff = computed(() => {
  const before = parseJson(props.record?.before_json)
  const after = parseJson(props.record?.after_json)
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
  return keys.map(key => ({
    key,
    label: fieldLabel(key, props.definition),
    before: formatFieldValue(before[key], props.definition, key),
    after: formatFieldValue(after[key], props.definition, key),
  }))
})

function parseJson(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'string') return {}
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function isList(value: unknown): value is Array<Record<string, unknown>> {
  return Array.isArray(value) && value.every(item => item && typeof item === 'object')
}

function listLabel(key: string): string {
  if (key === 'conversation') return '沟通记录'
  if (key === 'login_history') return '最近登录'
  return fieldLabel(key, props.definition)
}

function optionsFor(key: string): SelectOption[] | undefined {
  return props.definition.columns.find(column => column.key === key)?.options
}

function valueTone(key: string, value: unknown): string {
  const raw = String(value ?? '')
  const option = optionsFor(key)?.find(item => String(item.value) === raw)
  return statusTone(option?.label ?? raw)
}

function initials(): string {
  return avatarText(title.value)
}

function toneClass(): string {
  return avatarTone(title.value)
}

function close(): void {
  emit('close')
}

async function copyRequestNo(): Promise<void> {
  const value = String(props.record?.request_no ?? '')
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    ElMessage.success(`请求编号 ${value} 已复制`)
  } catch {
    ElMessage.info(`请求编号：${value}`)
  }
}
</script>

<template>
  <el-drawer :model-value="open" :size="'600px'" :with-header="false" destroy-on-close class="detail-drawer" @close="close">
    <div class="side-detail">
      <header class="drawer-head detail-drawer__head">
        <div class="drawer-head__text">
          <span class="detail-eyebrow">{{ definition.title }} · 详情</span>
          <h2 class="drawer-title">{{ title }}</h2>
          <p class="drawer-sub">{{ subtitle || definition.purpose }}</p>
        </div>
        <button type="button" class="icon-button" aria-label="关闭详情" @click="close"><AppIcon name="close" :size="18" /></button>
      </header>

      <div class="drawer-body detail-drawer__body">
        <div class="detail-hero">
          <span class="avatar detail-avatar" :class="toneClass()">{{ initials() }}</span>
          <div class="detail-hero__main">
            <div class="detail-hero__name">{{ title }}</div>
            <p class="detail-hero__meta">{{ definition.group }} · 数据版本 {{ record?.row_version ?? 1 }}</p>
          </div>
          <div class="detail-hero__side">
            <span v-if="statusOption" class="tag" :class="`tag--${statusOption.tone}`">{{ statusOption.label }}</span>
          </div>
        </div>

        <section v-if="metrics.length" class="detail-metrics">
          <div v-for="metric in metrics" :key="metric.field" class="detail-metric">
            <span>{{ metric.label }}</span>
            <b>{{ metric.value }}</b>
          </div>
        </section>

        <div v-if="definition.notice" class="info-banner info-banner--brand detail-notice">
          <AppIcon name="info" :size="14" />
          <span>{{ definition.notice }}</span>
        </div>

        <section v-for="section in sections" :key="section.title" class="detail-section">
          <h3 class="detail-section__title">{{ section.title }}</h3>
          <div class="detail-grid">
            <template v-for="field in section.fields" :key="field.key">
              <div
                v-if="isList(field.value)"
                class="detail-item detail-item--full"
              >
                <span class="detail-item__label">{{ listLabel(field.key) }}</span>
                <div class="mini-list">
                  <div v-for="(item, index) in field.value" :key="index" class="mini-list__row">
                    <b>{{ item.role || item.result || item.label || '记录' }}</b>
                    <span>{{ item.text || item.device || item.detail || '—' }}</span>
                    <small>{{ item.at ? formatDateTime(item.at) : '' }}</small>
                  </div>
                </div>
              </div>
              <div v-else class="detail-item" :class="{ 'detail-item--full': ['address', 'content', 'answer', 'indication', 'usage', 'precautions', 'description', 'detail'].includes(field.key) }">
                <span class="detail-item__label">{{ field.label }}</span>
                <div class="detail-item__value" :class="{ 'is-status': optionsFor(field.key) }">
                  <span v-if="optionsFor(field.key)" class="tag" :class="`tag--${valueTone(field.key, field.value)}`">{{ field.display }}</span>
                  <span v-else-if="Array.isArray(field.value)" class="tag-row">
                    <span v-for="item in field.value" :key="String(item)" class="tag tag--neutral tag--plain">{{ String(item) }}</span>
                  </span>
                  <span v-else-if="field.value === true" class="tag tag--success">是</span>
                  <span v-else-if="field.value === false" class="tag tag--neutral">否</span>
                  <span v-else>{{ field.display }}</span>
                </div>
              </div>
            </template>
          </div>
        </section>

        <section v-if="diff.length" class="detail-section">
          <h3 class="detail-section__title">变更差异</h3>
          <table class="diff-table">
            <thead><tr><th>字段</th><th>变更前</th><th>变更后</th></tr></thead>
            <tbody>
              <tr v-for="item in diff" :key="item.key">
                <td>{{ item.label }}</td>
                <td class="diff-old">{{ item.before }}</td>
                <td class="diff-new">{{ item.after }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section v-if="record?.note" class="detail-section">
          <h3 class="detail-section__title">备注</h3>
          <p class="detail-note">{{ record.note }}</p>
        </section>
      </div>

      <footer class="drawer-foot">
        <span class="drawer-foot__hint">仅展示当前角色可见字段，敏感信息默认脱敏</span>
        <button v-if="record?.request_no" type="button" class="btn" @click="copyRequestNo">
          <AppIcon name="audit" :size="14" />复制请求编号
        </button>
        <button type="button" class="btn" @click="close">关闭</button>
        <button v-if="canEdit && record" type="button" class="btn btn--primary" @click="emit('edit', record)">
          <AppIcon name="edit" :size="14" />编辑
        </button>
      </footer>
    </div>
  </el-drawer>
</template>

<style scoped>
.side-detail { display: flex; flex-direction: column; height: 100%; min-height: 0; }
.detail-drawer__head { padding: 18px 22px 14px; border-bottom: 1px solid var(--c-divider); }
.detail-drawer__body { padding: 18px 22px 24px; overflow: auto; flex: 1; }
.detail-eyebrow { display: block; color: var(--c-muted); font-size: 10px; letter-spacing: .4px; margin-bottom: 4px; }
.detail-avatar { width: 48px; height: 48px; font-size: 14px; }
.detail-hero__main { min-width: 0; }
.detail-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 16px; }
.detail-metric { padding: 10px 11px; border-radius: 9px; background: var(--c-surface-muted); border: 1px solid var(--c-border); }
.detail-metric span { display: block; font-size: 9.5px; color: var(--c-muted); }
.detail-metric b { display: block; margin-top: 4px; font-size: 15px; color: var(--c-ink); font-family: var(--font-num); }
.detail-notice { margin-bottom: 16px; }
.detail-item__value.is-status { padding-top: 1px; }
.mini-list { border: 1px solid var(--c-divider); border-radius: 8px; overflow: hidden; }
.mini-list__row { display: grid; grid-template-columns: 56px minmax(0, 1fr) auto; gap: 8px; padding: 7px 9px; border-bottom: 1px solid var(--c-divider); font-size: 10.5px; }
.mini-list__row:last-child { border-bottom: 0; }
.mini-list__row b { color: var(--c-text); }
.mini-list__row span { color: var(--c-muted); }
.mini-list__row small { color: var(--c-faint); }
.detail-note { padding: 10px 12px; border-radius: 8px; border: 1px solid var(--c-border); background: var(--c-surface-muted); color: var(--c-muted); font-size: 11.5px; line-height: 1.65; white-space: pre-wrap; }
@media (max-width: 767px) { .detail-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
